import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import Faqs from "../components/Faqs";
import FinalCTA from "../components/FinalCTA";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Hero from "../components/Hero";
import TrustedBy from "../components/TrustedBy";
import Pricing from "../components/Pricing";
import SectorSelector from "../components/SectorSelector";
import TransitionBlock from "../components/TransitionBlock";
import TransitionBlock2 from "../components/TransitionBlock2";
import VisionBlock from "../components/VisionBlock";
import Benefits from "../components/Benefits";
import WhatsappFloat from "../components/WhatsappFloat";
import { Helmet } from "react-helmet-async";
import CookiesBanner from "../components/CookiesBanner";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function MainPage() {
	const [showButton, setShowButton] = useState(false);
	const { t } = useTranslation();
	const { hash } = useLocation();

	useEffect(() => {
		if (hash) {
			const id = hash.replace("#", "");
			const element = document.getElementById(id);
			if (element) {
				const top =
					element.getBoundingClientRect().top + window.scrollY - 110;
				window.scrollTo({ top, behavior: "smooth" });
			}
		}
	}, [hash]);

	useEffect(() => {
		const handleScroll = () => {
			setShowButton(window.scrollY > 300);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div className="overflow-x-hidden">
			<Helmet>
				<title>{t("seo.main.title")}</title>

				<meta name="description" content={t("seo.main.description")} />
				<meta name="keywords" content={t("seo.main.keywords")} />
				<meta name="author" content={t("seo.main.author")} />

				{/* Open Graph */}
				<meta property="og:title" content={t("seo.main.ogTitle")} />
				<meta property="og:description" content={t("seo.main.ogDescription")} />
				<meta
					property="og:image"
					content="https://applaby.com/assets/applaby-og.png"
				/>
				<meta property="og:url" content="https://applaby.com" />
				<meta property="og:type" content="website" />

				{/* Twitter Card */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content={t("seo.main.twitterTitle")}
				/>
				<meta
					name="twitter:description"
					content={t("seo.main.twitterDescription")}
				/>
				<meta
					name="twitter:image"
					content="https://applaby.com/assets/applaby-og.png"
				/>

				{/* JSON-LD: SoftwareApplication */}
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "SoftwareApplication",
						name: "Applaby",
						operatingSystem: "iOS, Android, Web",
						applicationCategory: "BusinessApplication",
						offers: {
							"@type": "Offer",
							price: "29.9",
							priceCurrency: "EUR",
						},
						description: t("seo.main.schemaDescription"),
						url: "https://applaby.com",
						publisher: {
							"@type": "Organization",
							name: "Applaby",
							logo: {
								"@type": "ImageObject",
								url: "https://applaby.com/logo512.png",
							},
						},
					})}
				</script>

				{/* JSON-LD: Organization */}
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Organization",
						name: "Applaby",
						url: "https://applaby.com",
						logo: "https://applaby.com/logo512.png",
						sameAs: ["https://www.instagram.com/applaby_es"],
						founder: {
							"@type": "Organization",
							name: "Yowlu SL",
							url: "https://yowlu.com",
						},
						description: t("seo.main.organizationDescription"),
					})}
				</script>

				{/* JSON-LD: FAQPage */}
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "FAQPage",
						mainEntity: [
							{
								"@type": "Question",
								name: t("faqs.items.0.question"),
								acceptedAnswer: {
									"@type": "Answer",
									text: t("faqs.items.0.answer"),
								},
							},
							{
								"@type": "Question",
								name: t("faqs.items.5.question"),
								acceptedAnswer: {
									"@type": "Answer",
									text: t("faqs.items.5.answer"),
								},
							},
						],
					})}
				</script>
			</Helmet>

			<Header />
			<Hero />
			<TrustedBy />
			<Benefits />
			<TransitionBlock />
			<SectorSelector />
			<TransitionBlock2 />
			<VisionBlock />
			<Pricing />
			<FinalCTA />
			<Faqs />
			<Footer />
			<CookiesBanner />

			{/* Botones flotantes */}
			<div className="fixed bottom-6 right-6 flex flex-col gap-6 z-50">
				{showButton && (
					<motion.button
						onClick={scrollToTop}
						initial={{ opacity: 0, y: 50 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 50 }}
						transition={{ duration: 0.3 }}
						className="w-14 h-14 flex items-center justify-center rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 transition"
					>
						<ChevronUp className="w-6 h-6" />
					</motion.button>
				)}
				<WhatsappFloat />
			</div>
		</div>
	);
}

export default MainPage;
